<script setup>
import { computed } from 'vue';

// One editor per column type. Every type in the backend's COLUMN_TYPES has a
// branch here; anything unrecognised falls through to a plain text input
// rather than becoming uneditable.
const props = defineProps({
  column: { type: Object, required: true },
  modelValue: { type: [String, Number, Boolean, Array, null], default: null },
  disabled: { type: Boolean, default: false },
});

const emit = defineEmits(['update:modelValue']);

const inputType = computed(() => {
  switch (props.column.column_type) {
    case 'number':
    case 'currency':
    case 'percent':
      return 'number';
    case 'date':
      return 'date';
    case 'datetime':
      return 'datetime-local';
    case 'email':
      return 'email';
    case 'phone':
      return 'tel';
    case 'link':
      return 'url';
    default:
      return 'text';
  }
});

const isSelect = computed(() => props.column.column_type === 'list');
const isMultiSelect = computed(() => props.column.column_type === 'multi_select');
const isCheckbox = computed(() => props.column.column_type === 'checkbox');
const isLongText = computed(() => props.column.column_type === 'long_text');
const options = computed(() => props.column.config?.options || []);

const selectedMulti = computed(() =>
  Array.isArray(props.modelValue) ? props.modelValue : []
);

const onInput = value => emit('update:modelValue', value);

const toggleMulti = option => {
  const current = selectedMulti.value;
  const next = current.includes(option)
    ? current.filter(item => item !== option)
    : [...current, option];
  emit('update:modelValue', next);
};

const baseClass =
  'w-full px-2 py-1 text-sm bg-transparent border rounded border-n-weak text-n-slate-12 disabled:opacity-60';
</script>

<template>
  <input
    v-if="isCheckbox"
    type="checkbox"
    :checked="!!modelValue"
    :disabled="disabled"
    @change="onInput($event.target.checked)"
  />

  <textarea
    v-else-if="isLongText"
    :value="modelValue"
    :disabled="disabled"
    rows="3"
    :class="baseClass"
    @input="onInput($event.target.value)"
  />

  <select
    v-else-if="isSelect"
    :value="modelValue"
    :disabled="disabled"
    :class="baseClass"
    @change="onInput($event.target.value)"
  >
    <option value="" />
    <option v-for="option in options" :key="option" :value="option">
      {{ option }}
    </option>
  </select>

  <div v-else-if="isMultiSelect" class="flex flex-wrap gap-1">
    <button
      v-for="option in options"
      :key="option"
      type="button"
      :disabled="disabled"
      class="px-2 py-0.5 text-xs border rounded border-n-weak"
      :class="
        selectedMulti.includes(option)
          ? 'bg-n-brand text-white'
          : 'text-n-slate-11'
      "
      @click="toggleMulti(option)"
    >
      {{ option }}
    </button>
  </div>

  <input
    v-else
    :type="inputType"
    :value="modelValue"
    :disabled="disabled"
    :min="column.config?.min"
    :max="column.config?.max"
    :class="baseClass"
    @input="onInput($event.target.value)"
  />
</template>
